import {Injectable} from '@angular/core';
import {Observable, of, switchMap, map} from 'rxjs';
import { HttpClient } from '@angular/common/http';
import {environment} from '@env/environment';
import {Check} from '@app/domain/check';
import {
    AddIdPedido,
    ClearIdPedido,
    SetCuentaPorCobrar
} from '@app/state/restaurant/cobrador/cobrador-restaurante.actions';
import {Store} from '@ngxs/store';
import {Router} from '@angular/router';
import {DomSanitizer} from '@angular/platform-browser';
import {Printer} from '@app/domain/printer';
import {promiseBlob2Data} from '@app/utils/util-functions';
import {DataSend} from '@app/domain/data-send';
import {CoffeEscPosService} from '@app/service/coffe-esc-pos.service';
import {DatePipe} from '@angular/common';

export interface CheckFilters {
    pagado: boolean;
    idPuntoVenta: number;
    fecha: Date;
    tipoPedido?: any[];
    idPedido?: any[];
    numeroOrden?: any[];
}

@Injectable({
    providedIn: 'root'
})
export class CheckService {

    baseUrl: string = environment.endpointApi;

    constructor(private http: HttpClient, private store: Store,
                private router: Router, private sanitizer: DomSanitizer,
                private coffeEscPosService: CoffeEscPosService,
                private datePipe: DatePipe) {
    }

    findById(id): Observable<Check> {
        return this.http.get<Check>(`${this.baseUrl}/checks/${id}`);
    }

    findAll(params): Observable<Check[]> {
        return this.http.get<Check[]>(`${this.baseUrl}/checks?${params}`);
    }

    findAllFiltered(filters: CheckFilters): Observable<Check[]> {
        return this.findAll(this.buildFilterParams(filters));
    }

    countOrdersFiltered(filters: CheckFilters): Observable<number> {
        return this.http.get<number>(`${this.baseUrl}/checks/count-orders?${this.buildFilterParams(filters)}`);
    }

    private buildFilterParams(filters: CheckFilters): string {
        const params = [
            `pagado=${filters.pagado}`,
            `idPuntoVenta=${filters.idPuntoVenta}`,
            `fecha=${this.datePipe.transform(filters.fecha, 'dd-MM-yyyy')}`
        ];
        if (filters.tipoPedido) {
            filters.tipoPedido.forEach(tipo => params.push(`tipoPedido=${tipo}`));
        }
        if (filters.idPedido) {
            filters.idPedido.forEach(id => params.push(`idPedido=${id}`));
        }
        if (filters.numeroOrden) {
            filters.numeroOrden.forEach(numero => params.push(`numeroOrden=${numero}`));
        }
        return params.join('&');
    }

    update(id, model: Check): Observable<Check> {
        return this.http.put<Check>(`${this.baseUrl}/checks/${id}`, model);
    }

    goToPay(checks: Check[]) {
        if (checks) {
            const orderNumbers = [...new Set(checks
                .map(c => c.pedido?.numeroOrden)
                .filter(numero => numero !== undefined && numero !== null))];
            if (checks.length === 1) {
                this.store.dispatch(new SetCuentaPorCobrar(checks[0]));
            } else {
                this.store.dispatch(new ClearIdPedido());
                orderNumbers.forEach(numero => this.store.dispatch(new AddIdPedido(numero)));
            }
            this.router.navigateByUrl('/restaurante/cobrador/cuentas');
        }
    }

    getUrlPdf(idCuenta) {
        return `${environment.endpointApi}/public/pdf/check-register/${idCuenta}`;
    }


    openPdf(cuenta: Check, options): Observable<any> {
        const url = this.getUrlPdf(cuenta.id);
        return this.http.get(url, {responseType: 'blob'}).pipe(
            switchMap(blob => promiseBlob2Data(blob)),
            switchMap(data => {
                const response: any = {
                    urlViewer: this.sanitizer.bypassSecurityTrustResourceUrl('/assets/pdfjs/web/viewer.html?file=' + data.objectUrl),
                    url: data.objectUrl,
                    urlSafe: this.sanitizer.bypassSecurityTrustResourceUrl(url),
                    base64: data.base64,
                    originalUrl: url,
                    check: cuenta
                };
                return options.escPos
                    ? this.coffeEscPosService.checkEscPos(cuenta.id, options).pipe(map(escPos => ({...response, escPos})))
                    : of(response);
            })
        );
    }


    getDataSend(urlPdf, cuenta: Check): DataSend {
        const url = `${urlPdf}&time=${new Date().getTime()}`;
        return  {
            id: cuenta.id,
            nameMessage: `Tu cuenta ha sido generada-${new Date().getTime()}.pdf`,
            archivos: [],
            asunto: `Cuenta de consumo, mesa ${cuenta.pedido.mesa.numero}`,
            emails: [],
            number: '',
            texto: '',
            url,
            messageWhatsappWeb: `Hola! se le envía su cuenta de consumo de la mesa ${cuenta.pedido.mesa.numero}: ${url}`,
            textMessage: `Hola! se le envía su cuenta de consumo de la mesa ${cuenta.pedido.mesa.numero}`,
            type: 'ticket'
        };
    }
}
